import { useAuth0 } from '@auth0/auth0-react';
import { yupResolver } from '@hookform/resolvers/yup';
import type { ProfileForm } from '@dtp/membership-service-types';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useStateMachine } from 'little-state-machine';
import { HolderFormType } from '@appTypes/internationalDrivingPermitFormType';
import { NAFGTMEvent } from '@appTypes/GTM/NAFGTMEvent';
import { useSendGTMEventOnce } from '@hooks/GTM/useSendGTMEventOnce';
import { useFetchCity } from '@hooks/useFetchCity';
import { useServerErrors } from '@hooks/useServerErrors';
import { useEmailValidation } from '@shared/hooks/useEmailValidation';
import useSelector from '@redux/typedHooks';
import { toPhoneNumberValue } from '../../../../lib/phoneValidation';
import { createServerFieldMappings } from '../../lib/createServerFieldMappings';
import { updateAction } from '../../lib/updateAction';
import { createHolderSchema } from '../../schemas/HolderSchema';
import { useTrackingStep2 } from '../../utils/useIDPTracking';

interface HolderFormLogicOptions {
  profile?: ProfileForm;
  goToStep?: (step: number) => void;
}

const stripDisplayFields = (formData: HolderFormType) => {
  const data = { ...formData };
  delete data.membershipSelection;
  return data;
};

export const useHolderFormLogic = ({ profile, goToStep }: HolderFormLogicOptions) => {
  const [profileIsLoaded, setProfileIsLoaded] = useState(false);
  const { isAuthenticated } = useAuth0();
  const serverErrors = useSelector((state) => state.order.validation?.validationResults);
  const {
    actions,
    state: { internationalDrivingPermitForm, idpCustomerForm },
  } = useStateMachine({ updateAction });
  const membershipSelection =
    internationalDrivingPermitForm?.membership === null
      ? 'no-membership'
      : internationalDrivingPermitForm?.membership?.productId || '';
  const product = useSelector(
    (state) =>
      (internationalDrivingPermitForm?.product?.productId &&
        state.internationalDrivingPermit.data.eligibleProducts?.[internationalDrivingPermitForm.product.productId]) ||
      undefined,
  );
  const holderSchema = useMemo(() => createHolderSchema(!isAuthenticated), [isAuthenticated]);
  const form = useForm<HolderFormType>({ resolver: yupResolver(holderSchema), mode: 'onChange' });
  const {
    clearErrors,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = form;
  const zipCode = watch('zip');
  const emailValue = watch('eMail');
  const { cityName, cityError } = useFetchCity(zipCode);
  const { isValid: isEmailValid, isLoading: isEmailValidating } = useEmailValidation(emailValue || '', !!emailValue);

  const persistForm = (data: HolderFormType) => {
    actions.updateAction({ formName: 'idpCustomerForm', data: stripDisplayFields(data) });
  };

  useEffect(() => {
    if (!cityName) return;
    setValue('city', cityName);
    if (!cityError) {
      clearErrors('city');
      return;
    }
    setError('city', { type: 'manual', message: 'Vi klarte ikke oppdatere poststed. Kontakt kundeservice' });
  }, [cityName, cityError, clearErrors, setValue, setError]);

  useEffect(() => {
    if (internationalDrivingPermitForm && !internationalDrivingPermitForm.product && goToStep) goToStep(1);
  }, [internationalDrivingPermitForm, goToStep]);

  useEffect(() => {
    if (!profile || profileIsLoaded) return;
    const birthDate = profile.dateOfBirth?.value ? new Date(profile.dateOfBirth.value) : null;
    reset({
      membershipSelection,
      phoneNumber:
        idpCustomerForm?.phoneNumber ||
        toPhoneNumberValue(profile.phoneNumber?.value, profile.phoneNumberPrefix?.value),
      firstName: idpCustomerForm?.firstName || profile.firstName?.value || '',
      lastName: idpCustomerForm?.lastName || profile.lastName?.value || '',
      eMail: idpCustomerForm?.eMail || profile.email?.value || '',
      birthDate: {
        day: idpCustomerForm?.birthDate?.day || (birthDate ? birthDate.getDate() : ('' as any)),
        month: idpCustomerForm?.birthDate?.month || (birthDate ? birthDate.getMonth() + 1 : ('' as any)),
        year: idpCustomerForm?.birthDate?.year || (birthDate ? birthDate.getFullYear() : ('' as any)),
      },
      gender: idpCustomerForm?.gender || profile.gender?.value || '',
      address: idpCustomerForm?.address || profile.address?.value || '',
      zip: idpCustomerForm?.zip || profile.postalCode?.value || '',
      city: idpCustomerForm?.city || profile.city?.value || '',
      cityOfBirth: idpCustomerForm?.cityOfBirth || '',
      countryOfBirth: idpCustomerForm?.countryOfBirth || '',
    });
    setProfileIsLoaded(true);
  }, [profile, profileIsLoaded, idpCustomerForm, membershipSelection, reset]);

  const numberOfProducts = useMemo(
    () => internationalDrivingPermitForm?.product?.amount && Number(internationalDrivingPermitForm.product.amount),
    [internationalDrivingPermitForm?.product?.amount],
  );

  const serverFieldMap = useMemo(
    () => (typeof numberOfProducts === 'number' ? createServerFieldMappings(numberOfProducts).form2 : undefined),
    [numberOfProducts],
  );
  useServerErrors<HolderFormType>(serverErrors, serverFieldMap, setError);

  useSendGTMEventOnce(
    product && typeof internationalDrivingPermitForm?.product?.amount === 'number'
      ? {
          event: NAFGTMEvent.addShippingInfo,
          ecommerce: {
            items: [
              {
                item_id: product.productNumber,
                quantity: internationalDrivingPermitForm.product.amount,
                item_name: product.productName,
                price: (
                  product.productAppPrice[internationalDrivingPermitForm.product.amount].itemTotalPrice /
                  internationalDrivingPermitForm.product.amount
                ).toFixed(2),
                item_category2: 'Internasjonalt Førerkort',
                item_category3: 'Bestill',
              },
            ],
          },
        }
      : undefined,
    [product, internationalDrivingPermitForm?.product?.amount],
  );
  useTrackingStep2(internationalDrivingPermitForm?.product?.productId);

  return {
    form,
    errors,
    isAuthenticated,
    idpCustomerForm,
    internationalDrivingPermitForm,
    onSubmit: persistForm,
    onGoBack: persistForm,
    isValid: Object.values(errors).length === 0 && !isEmailValidating && isEmailValid !== false,
  };
};
